import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle, Clock, Truck, Package, ChefHat, ArrowLeft, Phone } from 'lucide-react';
import api from '@/lib/api';

const STATUSES = [
  { key: 'confirmed', label: 'Order Confirmed', icon: CheckCircle },
  { key: 'preparing', label: 'Being Prepared', icon: ChefHat },
  { key: 'ready', label: 'Ready for Pickup', icon: Package },
  { key: 'out_for_delivery', label: 'Out for Delivery', icon: Truck },
  { key: 'delivered', label: 'Delivered', icon: CheckCircle },
];

export default function OrderTracking() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const res = await api.get(`/orders/${id}`);
        setOrder(res.data);
      } catch {}
      setLoading(false);
    };
    fetchOrder();
    const interval = setInterval(fetchOrder, 10000);
    return () => clearInterval(interval);
  }, [id]);

  if (loading) return (
    <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#cc3737] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!order) return (
    <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center">
      <div className="text-center"><p className="text-white text-lg mb-4">Order not found</p><Link to="/shop" className="text-[#b56b38] hover:underline">Back to Shop</Link></div>
    </div>
  );

  const currentIndex = STATUSES.findIndex(s => s.key === order.status);
  const isCancelled = order.status === 'cancelled';

  return (
    <div className="min-h-screen bg-[#0B0E14]" data-testid="order-tracking-page">
      <nav className="fixed top-0 w-full z-50 bg-[#0B0E14]/70 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 flex items-center justify-between h-16">
          <Link to="/shop" className="flex items-center gap-2 text-[#94A3B8] hover:text-white transition-colors"><ArrowLeft className="w-4 h-4" /> Back to Shop</Link>
          <span className="text-white font-medium text-sm">Order #{order.order_number}</span>
          <div className="w-16" />
        </div>
      </nav>

      <div className="pt-24 pb-12 max-w-2xl mx-auto px-6 sm:px-8">
        {/* Status Timeline */}
        <div className="bg-[#12151C] border border-white/10 rounded-xl p-6 mb-6" data-testid="status-timeline">
          <h2 className="font-['Playfair_Display'] text-xl text-white mb-6">
            {isCancelled ? 'Order Cancelled' : order.status === 'delivered' ? 'Order Delivered!' : 'Order Status'}
          </h2>

          {isCancelled ? (
            <div className="text-center py-6">
              <div className="w-12 h-12 rounded-full bg-[#EF4444]/20 flex items-center justify-center mx-auto mb-3">
                <span className="text-[#EF4444] text-xl">X</span>
              </div>
              <p className="text-[#EF4444] font-medium">This order has been cancelled</p>
            </div>
          ) : (
            <div className="space-y-6">
              {STATUSES.map((status, i) => {
                const isCompleted = i <= currentIndex;
                const isCurrent = i === currentIndex;
                return (
                  <div key={status.key} className="flex items-center gap-4" data-testid={`status-step-${status.key}`}>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${isCompleted ? 'bg-[#10B981]/20' : 'bg-[#1A1D27]'} ${isCurrent ? 'ring-2 ring-[#10B981]/50' : ''}`}>
                      <status.icon className={`w-5 h-5 ${isCompleted ? 'text-[#10B981]' : 'text-[#64748B]'}`} />
                    </div>
                    <div className="flex-1">
                      <p className={`text-sm font-medium ${isCompleted ? 'text-white' : 'text-[#64748B]'}`}>{status.label}</p>
                      {isCurrent && <p className="text-[#b56b38] text-xs mt-0.5">Current</p>}
                    </div>
                    {i < STATUSES.length - 1 && (
                      <div className={`hidden sm:block w-px h-6 ml-5 ${i < currentIndex ? 'bg-[#10B981]' : 'bg-[#1A1D27]'}`} />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Driver Info */}
        {order.driver_name && (
          <div className="bg-[#12151C] border border-white/10 rounded-xl p-5 mb-6" data-testid="driver-info">
            <h3 className="text-white font-medium mb-3">Your Driver</h3>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#cc3737]/20 flex items-center justify-center">
                <Truck className="w-5 h-5 text-[#cc3737]" />
              </div>
              <div>
                <p className="text-white font-medium text-sm">{order.driver_name}</p>
                <p className="text-[#64748B] text-xs">{order.status === 'out_for_delivery' ? 'On the way to you' : order.status === 'delivered' ? 'Delivered' : 'Preparing your order'}</p>
              </div>
            </div>
          </div>
        )}

        {/* Delivery OTP - shown to customer when driver is on the way */}
        {order.delivery_otp && order.status === 'out_for_delivery' && (
          <div className="bg-[#b56b38]/10 border border-[#b56b38]/30 rounded-xl p-5 mb-6 text-center" data-testid="delivery-otp-display">
            <p className="text-[#94A3B8] text-sm mb-2">Your Delivery Verification Code</p>
            <p className="text-white text-4xl font-mono font-bold tracking-[0.3em] mb-2" data-testid="delivery-otp-code">{order.delivery_otp}</p>
            <p className="text-[#64748B] text-xs">Share this code with your driver to confirm delivery</p>
          </div>
        )}

        {/* Proof Photos */}
        {(order.pickup_photo || order.delivery_photo) && (
          <div className="bg-[#12151C] border border-white/10 rounded-xl p-5 mb-6" data-testid="proof-photos">
            <h3 className="text-white font-medium mb-3">Proof Photos</h3>
            <div className="grid grid-cols-2 gap-3">
              {order.pickup_photo && (
                <div><p className="text-[#64748B] text-xs mb-1">Pickup Proof</p><img src={order.pickup_photo} alt="Pickup proof" className="w-full h-32 object-cover rounded-lg" /></div>
              )}
              {order.delivery_photo && (
                <div><p className="text-[#64748B] text-xs mb-1">Delivery Proof</p><img src={order.delivery_photo} alt="Delivery proof" className="w-full h-32 object-cover rounded-lg" /></div>
              )}
            </div>
          </div>
        )}

        {/* Order Details */}
        <div className="bg-[#12151C] border border-white/10 rounded-xl p-5" data-testid="order-details">
          <h3 className="text-white font-medium mb-4">Order Details</h3>
          <div className="space-y-3 mb-4">
            {order.items?.map((item, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-10 h-10 rounded bg-[#1A1D27] overflow-hidden flex-shrink-0">
                  {item.image && <img src={item.image} alt={item.name} className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm truncate">{item.name}</p>
                  <p className="text-[#64748B] text-xs">Qty: {item.quantity}</p>
                </div>
                <span className="text-white text-sm">${(item.price * item.quantity).toFixed(2)}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-white/5 pt-3 space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-[#64748B]">Subtotal</span><span className="text-white">${order.subtotal?.toFixed(2)}</span></div>
            <div className="flex justify-between"><span className="text-[#64748B]">Tax</span><span className="text-white">${order.tax?.toFixed(2)}</span></div>
            <div className="flex justify-between"><span className="text-[#64748B]">Delivery</span><span className="text-white">${order.delivery_fee?.toFixed(2)}</span></div>
            {order.tip > 0 && <div className="flex justify-between"><span className="text-[#64748B]">Tip</span><span className="text-white">${order.tip?.toFixed(2)}</span></div>}
            <div className="flex justify-between font-medium pt-1 border-t border-white/5"><span className="text-white">Total</span><span className="text-[#b56b38]">${order.total?.toFixed(2)}</span></div>
          </div>
          <div className="mt-4 pt-3 border-t border-white/5">
            <p className="text-[#64748B] text-xs"><span className="text-[#94A3B8]">Delivery to:</span> {order.delivery_address}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
